(function () {
  const LOG_PREFIX = '[cruise-photobook-builder]';
  const STORAGE_KEY = 'cruisePhotobookProjects';
  const STORED_PROJECT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // see architecture.md §8
  // Separate from STORAGE_KEY/STORED_PROJECT_TTL_MS above — that map is keyed by Shopify product.id and drives the
  // "Continue editing" panel for drafts not yet in the cart. This one is keyed by projectId (a customer can have
  // several photobook projects in the cart at once, all sharing the same photobook_product.id) and tracks "last
  // saved" for projects that ARE in the cart, per PrintBox's own 7-day-since-last-save retention window — written
  // here, pruned against the live cart in cart-reviews.js.liquid/photobook-addon-cart-sync.js (see architecture.md
  // §12.3, same dual UpCart/native-cart mechanism already built for the addon-line sync).
  const CART_PROJECT_DATES_KEY = 'cruisePhotobookCartProjectDates';
  // PrintBox's own "back" can drop our URL params.
  const PRE_EDITOR_URL_KEY = 'cruisePhotobookPreEditorUrl';
  // Params PrintBox's own routing writes into location.search as the customer picks options — stripped on exit.
  const PRINTBOX_ROUTING_PARAMS = ['projectId', 'route', 'map', 'theme', 'fromCart'];

  function log() {
    if (!window.CRUISE_DEBUG) return;
    const args = Array.prototype.slice.call(arguments);
    console.log.apply(console, [LOG_PREFIX].concat(args));
  }

  function warn() {
    const args = Array.prototype.slice.call(arguments);
    console.warn.apply(console, [LOG_PREFIX].concat(args));
  }

  function error() {
    const args = Array.prototype.slice.call(arguments);
    console.error.apply(console, [LOG_PREFIX].concat(args));
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // "YYYY-MM-DD" -> "DD Month YYYY", for spine_text. Plain component split, not `new Date()` — see architecture.md §5.
  const SPINE_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function formatSpineDate(isoDate) {
    const parts = (isoDate || '').split('-');
    if (parts.length !== 3) return '';
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!year || !month || !day || month < 1 || month > 12) return '';
    return day + ' ' + SPINE_MONTH_NAMES[month - 1] + ' ' + year;
  }

  // #shipName's value is "SHIP NAME - Cruise Line" (the selectize dropdown's own display format). Client
  // request 2026-08-10: spine_text should show just the ship name (the caps part), not the cruise line suffix —
  // scoped to spine_text only, components.ship_name itself is untouched (a different, separately-confirmed key).
  function stripCruiseLineFromShipName(value) {
    return (value || '').split(' - ')[0].trim();
  }

  // Drops "[1995-2017]" / "[Launched 2020]" from the ship name, for the PrintBox components only (cart keeps them).
  function stripBracketsFromShipName(value) {
    return (value || '').replace(/\s*\[[^\]]*\]\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // projectId is a query param inside the SDK's own hash route, not the whole hash.
  function getProjectIdFromHash() {
    const hash = window.location.hash || '';
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) return '';
    return new URLSearchParams(hash.slice(queryIndex + 1)).get('projectId') || '';
  }

  // Per-product map { productId: { projectId, additionalPages } } — a draft on one product shouldn't show as "continue" on another.
  function readStoredProjects() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      warn('failed to read/parse stored photobook projects, treating as empty', e);
      return {};
    }
  }

  function writeStoredProjects(stored) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {
      warn('failed to write stored photobook projects', e);
    }
  }

  // Missing/unparseable savedAt (e.g. an entry written before this existed) is treated as expired, not exempt.
  function getStoredProject(productId) {
    const stored = readStoredProjects();
    const entry = stored[productId];
    if (!entry) return null;
    if (!entry.savedAt || Date.now() - entry.savedAt > STORED_PROJECT_TTL_MS) {
      log('stored project for product', productId, 'expired (>7d), clearing', entry);
      delete stored[productId];
      writeStoredProjects(stored);
      return null;
    }
    return entry;
  }

  function getStoredProjectId(productId) {
    const stored = getStoredProject(productId);
    return stored ? stored.projectId : '';
  }

  function getStoredAdditionalPages(productId) {
    const stored = getStoredProject(productId);
    return stored ? (stored.additionalPages || 0) : 0;
  }

  function getStoredCruiseSnapshot(productId) {
    const stored = getStoredProject(productId);
    return stored ? (stored.cruiseSnapshot || null) : null;
  }

  // cruiseSnapshot ties the cart line's properties to what's actually baked into the project, not the live DOM.
  function setStoredProject(productId, projectId, additionalPages, cruiseSnapshot) {
    const stored = readStoredProjects();
    stored[productId] = {
      projectId: projectId,
      additionalPages: additionalPages || 0,
      cruiseSnapshot: cruiseSnapshot || null,
      savedAt: Date.now()
    };
    writeStoredProjects(stored);
    log('stored project for product', productId, '->', stored[productId]);
  }

  function clearStoredProjectId(productId) {
    const stored = readStoredProjects();
    if (!(productId in stored)) return;
    delete stored[productId];
    writeStoredProjects(stored);
    log('cleared stored projectId for product', productId);
  }

  // { [projectId]: savedAt } — see CART_PROJECT_DATES_KEY above. Pruned elsewhere (cart-reviews.js.liquid /
  // photobook-addon-cart-sync.js), only ever written here.
  function touchCartProjectDate(projectId) {
    if (!projectId) return;
    let stored;
    try {
      stored = JSON.parse(window.localStorage.getItem(CART_PROJECT_DATES_KEY) || '{}') || {};
    } catch (e) {
      warn('failed to read/parse cart project dates, treating as empty', e);
      stored = {};
    }
    stored[projectId] = Date.now();
    try {
      window.localStorage.setItem(CART_PROJECT_DATES_KEY, JSON.stringify(stored));
      log('touched cart project date for', projectId, '->', stored[projectId]);
    } catch (e) {
      warn('failed to write cart project dates', e);
    }
  }

  ready(function () {
    const config = window.CruisePhotobookBuilderConfig;
    if (!config) {
      warn('window.CruisePhotobookBuilderConfig missing, skipping mount');
      return;
    }

    log('config:', config);

    // Album mode's per-option (Theme/Cover/Format/...) selectors, see architecture.md §13.
    const optionSelectorsRoot = document.getElementById('PhotobookOptionSelectors-' + config.sectionId);
    const priceEl = document.getElementById('price-' + config.sectionId);

    // Resolves the checked combination to a photobook_product variant — see architecture.md §13.
    function resolvePhotobookVariant() {
      if (!optionSelectorsRoot || !config.photobookVariants) return null;
      const selections = [];
      optionSelectorsRoot.querySelectorAll('[data-photobook-variant-option-index]').forEach(function (group) {
        const idx = parseInt(group.dataset.photobookVariantOptionIndex, 10);
        const checked = group.querySelector('input:checked');
        if (checked) selections[idx] = checked.value;
      });
      return config.photobookVariants.find(function (v) {
        return v.options.length === selections.length && v.options.every(function (val, i) { return val === selections[i]; });
      }) || null;
    }

    // Switches the gallery/modal to photobook_gallery_product's variant media — same <media-gallery>
    // setActiveMedia()/modal-prepend mechanism Dawn's own variant picker uses.
    function switchGalleryToPhotobookVariant(variant) {
      if (!config.useAlbumGalleryImages) return;

      const featuredMediaId = variant && variant.featuredMediaId;
      if (!featuredMediaId) return;

      const mediaId = config.sectionId + '-pbx-' + featuredMediaId;
      // <media-gallery> may not be upgraded yet on the initial on-load call.
      customElements.whenDefined('media-gallery').then(function () {
        document.querySelectorAll('[id^="MediaGallery-' + config.sectionId + '"]').forEach(function (mediaGallery) {
          if (typeof mediaGallery.setActiveMedia !== 'function') return;
          // setActiveMedia() assumes a match and throws otherwise — guard instead of letting that abort the
          // rest of this forEach/the caller's own remaining code.
          if (!mediaGallery.querySelector('[data-media-id="' + mediaId + '"]')) {
            warn('switchGalleryToPhotobookVariant(): no slide with data-media-id', mediaId, 'in', mediaGallery.id);
            return;
          }
          mediaGallery.setActiveMedia(mediaId, true);
        });
      });

      const modalContent = document.querySelector('#ProductModal-' + config.sectionId + ' .product-media-modal__content');
      if (!modalContent) return;
      // Raw media id, no -pbx- prefix — mirrors snippets/product-media.liquid's own data-media-id.
      const newMediaModal = modalContent.querySelector('[data-media-id="' + featuredMediaId + '"]');
      if (newMediaModal) modalContent.prepend(newMediaModal);
    }

    // Base image sync, independent of the priceEl-gated block below.
    if (optionSelectorsRoot) {
      function notifyPhotobookVariantChanged() {
        const variant = resolvePhotobookVariant();
        switchGalleryToPhotobookVariant(variant);
        document.dispatchEvent(new CustomEvent('cruise:photobook-variant-changed', { detail: { variant: variant } }));
      }

      optionSelectorsRoot.addEventListener('change', function (event) {
        const input = event.target;
        if (!input || input.type !== 'radio') return;
        notifyPhotobookVariantChanged();
      });

      // Fires once on load too, same reasoning as applyCheckedPrice() below.
      notifyPhotobookVariantChanged();
    }

    if (optionSelectorsRoot && priceEl) {
      // For values going into an HTML attribute inside the string-built markup below.
      function escapeAttr(value) {
        return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      }

      function applyCheckedPrice() {
        const variant = resolvePhotobookVariant();
        const price = variant && variant.price;
        if (!price) return;
        const compareAtPrice = variant.compareAtPrice;
        const onSale = !!compareAtPrice;
        // Black Friday promo: promo.js's checkPrice() keys off these, so they must survive our full rewrite.
        const promoAttrs = variant.promoDiscount
          ? ' custom-discount" data-promo-discount="' + variant.promoDiscount + (variant.promoPrice ? '" data-promo-price="' + escapeAttr(variant.promoPrice) : '')
          : '';
        // Same "From ..." wrapping snippets/price.liquid applies to its own money_price before reusing it in both
        // the regular and sale branches below — never applied to compareAtPrice (the struck-through price).
        const displayPrice = config.fromPriceTemplate ? config.fromPriceTemplate.replace('__PRICE__', price) : price;

        // Mirrors snippets/price.liquid's structure/classes so component-price.css styles it identically to a server-rendered sale/regular price.
        const html =
          '<div class="price price--large price--show-badge' + (onSale ? ' price--on-sale' : '') + '" data-photobook-price-variant="' + variant.id + '">' +
            '<div class="price__container' + promoAttrs + '">' +
              '<div class="price__regular">' +
                '<span class="visually-hidden visually-hidden--inline">' + config.regularPriceLabel + '</span>' +
                '<span class="price-item price-item--regular">' + displayPrice + '</span>' +
              '</div>' +
              '<div class="price__sale">' +
                (onSale
                  ? '<span class="visually-hidden visually-hidden--inline">' + config.regularPriceLabel + '</span>' +
                    '<span><s class="price-item price-item--regular">' + compareAtPrice + '</s></span>' +
                    '<span class="visually-hidden visually-hidden--inline">' + config.salePriceLabel + '</span>' +
                    '<span class="price-item price-item--sale price-item--last">' + displayPrice + '</span>'
                  : '') +
              '</div>' +
            '</div>' +
            (onSale
              ? '<span class="badge price__badge-sale color-' + config.saleBadgeColorScheme + '">' + config.saleBadgeText + '</span>'
              : '') +
          '</div>';
        // Marker, not an innerHTML compare: promo.js's checkPrice() edits our markup in place (.new-price), which must not count as Dawn overwriting it.
        const rendered = priceEl.querySelector('[data-photobook-price-variant]');
        if (rendered && rendered.getAttribute('data-photobook-price-variant') === String(variant.id)) return;
        log('photobook variant changed, updating price to', price);
        // Disconnect during our own write — MutationObserver fires as a microtask, so a boolean flag would already be reset by the time it runs.
        priceObserver.disconnect();
        priceEl.innerHTML = html;
        // Inside the disconnect, otherwise checkPrice()'s own .new-price write re-triggers this function.
        if (variant.promoDiscount && typeof window.checkPrice === 'function') window.checkPrice();
        priceObserver.observe(priceEl, { childList: true, subtree: true });
        // pdp-sticky-banner.js's own mirror doesn't react to this change on its own, see architecture.md §13.
        if (typeof window.updateIfBothChanged === 'function') window.updateIfBothChanged();
      }

      optionSelectorsRoot.addEventListener('change', function (event) {
        const input = event.target;
        if (!input || input.type !== 'radio') return;
        applyCheckedPrice();
      });

      // Dawn's native variant picker overwrites this same #price-* element asynchronously — re-apply ours on any change.
      const priceObserver = new MutationObserver(function () {
        applyCheckedPrice();
      });
      priceObserver.observe(priceEl, { childList: true, subtree: true });

      // Server-rendered price (main-product.liquid's 'price' block, use_variant: true) never gets the "From "
      // prefix — snippets/price.liquid only adds it when target == product, never for a variant. Previously this
      // relied on Dawn's own variant-picker AJAX call racing in and triggering priceObserver above, which is why
      // "From" was inconsistently missing on load (especially mobile Safari) until a variant was manually switched.
      applyCheckedPrice();
    }

    const openBtn = document.querySelector('[data-photobook-builder-open]');
    const continueBtn = document.querySelector('[data-photobook-builder-continue-open]');
    const continuePanel = document.querySelector('[data-photobook-builder-continue-panel]');
    const modal = document.getElementById('PhotobookBuilderModal-' + config.sectionId);

    // openBtn only — locks while a Live Preview render is still in flight (openEditor() needs its image url).
    let cruiseRenderPending = !!window.CruiseLivePreviewRenderPending;

    function updateOpenBtnLock() {
      if (!openBtn) return;
      openBtn.disabled = cruiseRenderPending;
    }

    document.addEventListener('cruise-live-preview:pending-changed', function (e) {
      cruiseRenderPending = !!e.detail;
      updateOpenBtnLock();
    });

    updateOpenBtnLock();

    // Opens the real cart drawer — doesn't touch the editor.
    const cartOnlyOpenBtn = continuePanel && continuePanel.querySelector('[data-photobook-panel-cart-only-open]');
    if (cartOnlyOpenBtn) {
      cartOnlyOpenBtn.addEventListener('click', function () {
        if (typeof window.upcartOpenCart === 'function') {
          window.upcartOpenCart();
        } else {
          window.location.href = '/cart';
        }
      });
    }

    // Toggles the "Pick up where you left off" panel's 4 states; async since cart count needs a live fetch.
    function updateContinuePanelState() {
      if (!continuePanel) return;

      const draftEntry = getStoredProject(config.productId);

      fetch('/cart.js')
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          const cartCount = (cart.items || []).filter(function (item) {
            return item.properties && item.properties['_pbx_project_uuid'] && item.properties['_pbx_addon'] !== 'true';
          }).length;

          const hasDraft = !!draftEntry;
          const hasCart = cartCount > 0;

          log('updateContinuePanelState()', { hasDraft: hasDraft, cartCount: cartCount, hasCart: hasCart });

          if (!hasDraft && !hasCart) {
            continuePanel.style.display = 'none';
            return;
          }

          const draftSection = continuePanel.querySelector('[data-photobook-panel-section="draft"]');
          const cartOnlySection = continuePanel.querySelector('[data-photobook-panel-section="cart-only"]');
          const separator = continuePanel.querySelector('[data-photobook-panel-separator]');
          const cartCombinedEl = continuePanel.querySelector('[data-photobook-panel-text="cart-combined"]');

          if (hasDraft) {
            const draftTextEl = continuePanel.querySelector('[data-photobook-panel-text="draft"]');
            if (draftTextEl && window.CruisePhotobookSavedBadge) {
              draftTextEl.textContent = window.CruisePhotobookSavedBadge.substitute(
                config.photobookPanelTemplates.draftText,
                { savedAt: draftEntry.savedAt }
              );
            }
            if (draftSection) draftSection.style.display = '';
          } else if (draftSection) {
            draftSection.style.display = 'none';
          }

          if (hasCart && !hasDraft) {
            // State 3 — cart items, no draft.
            const cartOnlyTextEl = continuePanel.querySelector('[data-photobook-panel-text="cart-only"]');
            if (cartOnlyTextEl && window.CruisePhotobookSavedBadge) {
              cartOnlyTextEl.textContent = window.CruisePhotobookSavedBadge.substitute(
                config.photobookPanelTemplates.cartOnlyText,
                { count: cartCount }
              );
            }
            if (cartOnlySection) cartOnlySection.style.display = '';
            if (separator) separator.style.display = 'none';
            if (cartCombinedEl) cartCombinedEl.style.display = 'none';
          } else if (hasCart) {
            // State 4 — both draft and cart items.
            if (cartCombinedEl && window.CruisePhotobookSavedBadge) {
              cartCombinedEl.textContent = window.CruisePhotobookSavedBadge.substitute(
                config.photobookPanelTemplates.cartCombinedText,
                { count: cartCount }
              );
              cartCombinedEl.style.display = '';
            }
            if (separator) separator.style.display = '';
            if (cartOnlySection) cartOnlySection.style.display = 'none';
          } else {
            // State 2 — draft only.
            if (cartOnlySection) cartOnlySection.style.display = 'none';
            if (separator) separator.style.display = 'none';
            if (cartCombinedEl) cartCombinedEl.style.display = 'none';
          }

          continuePanel.style.display = '';
        })
        .catch(function (err) {
          error('updateContinuePanelState: /cart.js fetch failed', err);
        });
    }

    // Minimal hidden <form> in album mode anchors the form="product-form-{sectionId}" fields; FormData collects them generically.
    function collectCruiseFormProperties() {
      const form = document.getElementById('product-form-' + config.sectionId);
      const properties = {};
      if (!form) {
        warn('collectCruiseFormProperties: #product-form-' + config.sectionId + ' not found, cart item will have no cruise details');
        return properties;
      }
      const formData = new FormData(form);
      formData.forEach(function (value, key) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      return properties;
    }

    // Same reset regular products do after cart add (main-product.liquid's own clearFields()), reimplemented — that one's a private closure, not reachable from here.
    function clearCruiseFields() {
      const fields = [
        document.getElementById('shipName'),
        document.getElementById('startPort'),
        document.getElementById('endPort'),
        document.getElementById('startDateId'),
        document.getElementById('endDateId'),
        document.getElementById('title'),
        document.getElementById('photobookTitle')
      ];
      fields.forEach(function (field) {
        if (!field) return;
        const selectizeInstance = window.jQuery && window.jQuery(field).data('selectize');
        if (selectizeInstance) {
          selectizeInstance.clear();
          selectizeInstance.blur();
        } else {
          field.value = '';
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (field._flatpickr) field._flatpickr.clear();
      });
      const startDatePlaceholder = document.getElementById('startDatePlaceholder');
      const endDatePlaceholder = document.getElementById('endDatePlaceholder');
      if (startDatePlaceholder) { startDatePlaceholder.style.display = 'block'; startDatePlaceholder.style.opacity = '1'; }
      if (endDatePlaceholder) { endDatePlaceholder.style.display = 'block'; endDatePlaceholder.style.opacity = '1'; }
      // Classic/Satellite switch copies of these fields — cleared too, or the next switch restores them (docs/url-prefill.md).
      ['view_switch_shipname', 'view_switch_startport', 'view_switch_endport'].forEach(function (key) { localStorage.removeItem(key); });
    }

    // Re-editing an already-cart-added project can change additionalPages — properties can't be patched via /cart/change.js, so the addon line is always dropped and re-added fresh rather than patched in place.
    function syncAddonForExistingProject(projectId) {
      return fetch('/cart.js').then(function (response) { return response.json(); }).then(function (cart) {
        const mainItem = cart.items.find(function (item) {
          return item.properties && item.properties['_pbx_project_uuid'] === projectId && item.properties['_pbx_addon'] !== 'true';
        });
        if (!mainItem) return;

        const addonItem = cart.items.find(function (item) {
          return item.properties && item.properties['_pbx_project_uuid'] === projectId && item.properties['_pbx_addon'] === 'true';
        });

        const removeExisting = addonItem
          ? fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: addonItem.key, quantity: 0 })
            })
          : Promise.resolve();

        return removeExisting.then(function () {
          if (lastKnownAdditionalPages <= 0 || !config.additionalPagesVariantId) return;
          const forLabel = (addonItem && addonItem.properties['For'])
            || (config.photobookProductTitle + (mainItem.properties['Ship Name'] ? ' — ' + mainItem.properties['Ship Name'] : ''));
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: config.additionalPagesVariantId,
                quantity: lastKnownAdditionalPages * mainItem.quantity,
                properties: {
                  _pbx_project_uuid: projectId,
                  _pbx_addon: 'true',
                  _pbx_additional_pages_per_unit: lastKnownAdditionalPages,
                  'For': forLabel
                }
              }]
            })
          });
        });
      });
    }

    // Assigned before setEditorConfig(), per SDK requirement.
    function assignCallbacks() {
      window.printbox = window.printbox || {};
      Object.assign(window.printbox, {
        authUserRequest: function () {
          warn('callback: authUserRequest() called — auth flow not implemented yet (guest/anonymous only)');
          return Promise.reject(new Error('Auth not implemented yet'));
        },
        goToCartFinished: function (projectId, data) {
          log('callback: goToCartFinished()', { projectId: projectId, data: data });
          if (window.CRUISE_DEBUG) try { log('goToCartFinished() data (full JSON):', JSON.stringify(data, null, 2)); } catch (e) { warn('goToCartFinished() data not JSON-serializable', e); }

          if (isEditingExistingCartItem) {
            // Already a cart line (opened via its own Edit link) — re-adding would duplicate it. additionalPages may have changed during this edit, so the addon line gets resynced below.
            log('editing an existing cart item, syncing addon then reopening the drawer');
            suppressContinueButton = true;
            clearCruiseFields();
            cleanCartActionUrl();

            return syncAddonForExistingProject(projectId).catch(function (err) {
              error('syncAddonForExistingProject failed', err);
            }).then(function () {
              deinitEditor();
              modal.hide();
              // UpCart doesn't auto-detect a bare /cart/change.js call (architecture.md §11).
              if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
              // Real drawer is the UpCart app's own, not Dawn's cart-drawer/cart-notification — its public API opens it.
              if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
              return true;
            });
          }

          const variantId = currentEditorVariantId;
          if (!variantId) {
            error('goToCartFinished: no photobook variant captured for this project (not opened via the page-count selector?), cannot add to cart');
            return Promise.resolve(false);
          }

          // Quantity comes from our own page's native selector, not data.project.quantity — PrintBox's own control is unused here.
          const quantityInput = document.getElementById('Quantity-' + config.sectionId);
          const quantity = (quantityInput && parseInt(quantityInput.value, 10)) || 1;

          // Prefer the project's own snapshot over the live DOM, which may since have been cleared/overwritten.
          const hasSnapshot = pendingCruiseSnapshot && pendingCruiseSnapshot.properties && Object.keys(pendingCruiseSnapshot.properties).length > 0;
          if (!hasSnapshot) {
            warn('goToCartFinished: no cruise-data snapshot for this project, falling back to live DOM (pre-fix draft, or a project with no personalized data at all)');
          }
          const properties = hasSnapshot ? Object.assign({}, pendingCruiseSnapshot.properties) : collectCruiseFormProperties();
          properties['_pbx_project_uuid'] = projectId;
          properties['_pbx_source_product'] = config.productTitle || '';
          properties['_pbx_cover_image_url'] = (pendingCruiseSnapshot && pendingCruiseSnapshot.coverImageUrl)
            || window.CruiseLivePreviewCompositeImageUrl || config.albumFallbackMapUrl || window.CruiseLivePreviewLastImageUrl || '';
          // Reopens this exact project on this exact product page — drives the cart drawer's future "Edit" button.
          properties['_pbx_edit_url'] = buildEditUrl(projectId);

          const items = [{
            id: variantId,
            quantity: quantity,
            properties: properties
          }];

          // Additional-pages addon — same project uuid, own marker. additionalPages is per album copy, so total quantity scales with quantity.
          // _pbx_additional_pages_per_unit is the per-copy value — cart-reviews.js.liquid re-reads it to rescale if the main line's quantity later changes.
          if (lastKnownAdditionalPages > 0 && config.additionalPagesVariantId) {
            const addonQuantity = lastKnownAdditionalPages * quantity;
            items.push({
              id: config.additionalPagesVariantId,
              quantity: addonQuantity,
              properties: {
                _pbx_project_uuid: projectId,
                _pbx_addon: 'true',
                _pbx_additional_pages_per_unit: lastKnownAdditionalPages,
                // Static at add-time; quantity itself (shown in the locked stepper) stays the live source of truth after any rescale.
                'For': config.photobookProductTitle + (properties['Ship Name'] ? ' — ' + properties['Ship Name'] : '')
              }
            });
          }

          // Same element Dawn's own product-form.js drives, for the drawer-instead-of-redirect below.
          const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: items,
              sections: cartUi ? cartUi.getSectionsToRender().map(function (s) { return s.id; }) : undefined,
              sections_url: window.location.pathname
            })
          })
            .then(function (response) {
              if (!response.ok) {
                return response.json().catch(function () { return {}; }).then(function (body) {
                  throw new Error('Cart AJAX add failed (' + response.status + '): ' + (body && body.description || 'unknown error'));
                });
              }
              return response.json();
            })
            .then(function (addedItem) {
              log('added photobook to cart', addedItem);
              // Only clear the draft reference after a confirmed successful add.
              suppressContinueButton = true;
              clearStoredProjectId(config.productId);
              touchCartProjectDate(projectId);
              updateContinuePanelState();
              clearCruiseFields();
              cleanCartActionUrl();
              // Ignoring data.redirectToCartAfterAddingProjectToCart — stay on the PDP and open the drawer instead, like Dawn's native add-to-cart.
              // Explicit deinit() first — modalClosed's own deinit is reactive and may run too late if the editor renders outside the modal's DOM.
              deinitEditor();
              modal.hide();
              if (cartUi) cartUi.renderContents(addedItem);
              return true;
            })
            .catch(function (err) {
              error('goToCartFinished: cart add failed', err);
              return false;
            });
        },
        savingFinished: function (projectId, isAutoSave, additionalData) {
          log('callback: savingFinished()', { projectId: projectId, isAutoSave: isAutoSave, additionalData: additionalData });
          if (projectId && isEditingExistingCartItem) {
            // Re-editing an already-cart-added project (via its own Edit link) — refresh its "last saved" date.
            // PrintBox's own project retention runs 7 days from the last save, not from creation, so every save
            // during a re-edit session needs to push this out, same as the draft-side STORED_PROJECT_TTL_MS below.
            touchCartProjectDate(projectId);
          } else if (projectId && !suppressContinueButton) {
            // Not a cart line, not suppressed — an in-progress draft. Surface/refresh "Continue editing" for it.
            setStoredProject(config.productId, projectId, lastKnownAdditionalPages, pendingCruiseSnapshot);
            updateContinuePanelState();
          }
          return Promise.resolve();
        },
        onError404Occurrence: function () {
          warn('callback: onError404Occurrence()');
          return Promise.resolve(false);
        },
        externalEventBusListener: function (eventData) {
          log('event:', eventData && eventData.eventName, eventData);
        },
        setupEcommercePricesIntegration: function () {
          // Shopify's rule: % off each unit in cents, fractional cent dropped (49 at 20% → 40, not 39). Blank/0 = no discount.
          function discountedUnitPrice(unitPrice, promoDiscount) {
            const percent = parseFloat(promoDiscount) || 0;
            const cents = Math.round(unitPrice * 100);
            return (cents - Math.floor(cents * percent / 100)) / 100;
          }

          return {
            getEcommerceProjectPrice: function (params) {
              if (window.CRUISE_DEBUG) try { log('getEcommerceProjectPrice() params (full JSON):', JSON.stringify(params, null, 2)); } catch (e) { log('getEcommerceProjectPrice() params:', params); }
              trackDraftProjectId(params && params.projectId);
              lastKnownAdditionalPages = (params && params.additionalPages) || 0;
              const variant = resolvePhotobookVariant();
              if (!variant || typeof variant.rawPrice !== 'number') return Promise.resolve(undefined); // no variant resolved yet — nothing to price, see architecture.md §5
              const quantity = (params && params.quantity) || 1;
              const additionalPages = (params && params.additionalPages) || 0;
              const addonUnitPrice = config.additionalPagesUnitPrice || 0;
              // Black Friday: discounted per unit like Shopify's automatic discount, so editor = cart = checkout.
              const mainLine = discountedUnitPrice(variant.rawPrice, variant.promoDiscount) * quantity;
              const addonLine = discountedUnitPrice(addonUnitPrice, config.additionalPagesPromoDiscount) * additionalPages * quantity;
              // No real gross/net split available (would need Shopify's own tax calc) — both set to the same total, same approximation the spec itself expects for this display-only price.
              const total = Math.round((mainLine + addonLine) * 100) / 100;
              log('getEcommerceProjectPrice() price breakdown:', {
                variantId: variant.id,
                variantOptions: variant.options,
                variantRawPrice: variant.rawPrice,
                variantPromoDiscount: variant.promoDiscount,
                additionalPages: additionalPages,
                addonUnitPrice: addonUnitPrice,
                addonPromoDiscount: config.additionalPagesPromoDiscount,
                quantity: quantity,
                mainLine: mainLine,
                addonLine: addonLine,
                total: total
              });
              return Promise.resolve({ gross: total, net: total });
            }
          };
        }
      });
      log('printbox callbacks assigned');
    }

    let editorBooted = false;
    let currentEditorProjectId = null; // null = not booted yet; '' = a new project; else a specific id
    let currentEditorVariantId = null; // captured once in openEditor(), page count is locked so it can't drift
    let currentEditorBlueprintHash = null; // resolvePhotobookVariant().blueprintHash at the same moment, sent as productVariantBlueprintId
    let lastKnownAdditionalPages = 0; // latest additionalPages seen from getEcommerceProjectPrice(), used at cart-add time
    let pendingCruiseSnapshot = null; // { properties, coverImageUrl } as of project creation; null falls back to live DOM
    let hashPollTimer = null;
    // A trailing autosave (e.g. PrintBox's own save-on-deinit) can fire savingFinished after goToCartFinished already hid the button — this blocks it from reappearing.
    let suppressContinueButton = false;
    // Set from the ?pbxEditCartItem=1 marker on _pbx_edit_url — the cart line already exists, goToCartFinished must not add a duplicate.
    let isEditingExistingCartItem = new URLSearchParams(window.location.search).get('pbxEditCartItem') === '1';

    // projectId exists before savingFinished's first real save — only tracks it, doesn't write the draft store yet.
    function trackDraftProjectId(projectId) {
      if (!projectId || projectId === currentEditorProjectId) return;
      log('tracking new projectId (not yet saved)', projectId);
      currentEditorProjectId = projectId;
    }

    // hashchange doesn't fire for the SDK's own pushState/replaceState updates — poll instead.
    function syncStoredProjectIdFromHash() {
      trackDraftProjectId(getProjectIdFromHash());
    }

    // Same field-to-param mapping generateLink() uses for classic/satellite, so a saved/returned-to URL restores the same fields.
    function buildCruiseFieldsUrl() {
      // config.pageUrl, not the live (possibly polluted) pathname.
      const url = new URL(config.pageUrl);
      new URL(window.location.href).searchParams.forEach(function (value, key) {
        url.searchParams.set(key, value);
      });
      const fields = {
        shipname: document.getElementById('shipName'),
        startport: document.getElementById('startPort'),
        endport: document.getElementById('endPort'),
        startdate: document.getElementById('startDateId'),
        enddate: document.getElementById('endDateId'),
        maptitle: document.getElementById('title'),
        photobookTitle: document.getElementById('photobookTitle')
      };
      Object.keys(fields).forEach(function (param) {
        if (fields[param]) url.searchParams.set(param, fields[param].value || '');
      });
      document.querySelectorAll('[data-photobook-tab-group]').forEach(function (group) {
        const checked = group.querySelector('input:checked');
        if (checked) url.searchParams.set('photobookTab_' + group.dataset.photobookTabGroup, checked.value);
      });
      return url;
    }

    function buildLogoRedirectUri() {
      return buildCruiseFieldsUrl().toString();
    }

    // For a cart line's "Edit" link — restores fields and, via the hash, re-opens this exact project (see architecture.md §13).
    function buildEditUrl(projectId) {
      const url = buildCruiseFieldsUrl();
      // Read back into isEditingExistingCartItem on load — tells goToCartFinished this line item already exists.
      url.searchParams.set('pbxEditCartItem', '1');
      url.hash = '?projectId=' + encodeURIComponent(projectId);
      return url.toString();
    }

    // Strips the fields/edit-mode restore params (and the SDK's own hash/path) once a cart action is done with them — pairs with clearCruiseFields().
    // photobookTab_* (page-count tier) is left alone — that selection stays valid/visible regardless of the cart action.
    function cleanCartActionUrl() {
      const url = new URL(window.location.href);
      url.pathname = new URL(config.pageUrl).pathname; // PrintBox's own routing can leave window.location.pathname polluted (e.g. .../editor)
      ['shipname', 'startport', 'endport', 'startdate', 'enddate', 'maptitle', 'photobookTitle', 'pbxEditCartItem'].concat(PRINTBOX_ROUTING_PARAMS).forEach(function (key) {
        url.searchParams.delete(key);
      });
      url.hash = '';
      history.replaceState(null, '', url.toString());
    }

    // projectId: '' for a brand-new project, or a specific uuid to reopen.
    function bootEditor(projectId) {
      assignCallbacks();
      editorBooted = true;
      currentEditorProjectId = projectId;

      clearInterval(hashPollTimer);
      hashPollTimer = setInterval(syncStoredProjectIdFromHash, 1500);

      const editorConfig = {
        projectId: projectId,
        productFamilyId: config.familyId,
        storeName: config.storeName,
        sessionId: '', // guest/anonymous
        currency: config.currency,
        // SDK hard-requires 'language' despite docs calling it deprecated.
        language: 'en',
        translationsLanguage: 'en',
        contentLanguage: 'en',
        locale: 'en-US',
        ecommerceUrlPart: config.pageUrl, // not the live pathname — PrintBox's router can leave it polluted
        ecommerceVariant: 'shopify',
        useIframe: false,
        // Without this the editor's logo click sends customers to PrintBox's own homepage.
        logoRedirectUri: buildLogoRedirectUri(),
        // PrintBox-confirmed fix (2026-08-11 reply) for a browser-back loop in the Smart Wizard flow: without
        // this, SmartWizardActions.exitFromSmart() falls back to StepActions.goToStep(previousViewStepId) —
        // which points at an editor step, not the PDP — so back from "Add More Photos" bounces back into the
        // editor once before a second back finally reaches the PDP. Forces the "exit to first step" path instead
        // of that stale previousViewStepId, so back leaves the editor on the second press, not the third.
        useFirstInsteadOfEditorStepIfNotInUrl: true
        // productId (legacy init path) dropped — spec says don't use it.
        // attributeValues: { map_style: config.mapStyle } — reverted, unconfirmed key crashes app init entirely.
      };

      // Only attempted for a brand-new project — an existing project must not get re-injected.
      if (!projectId) {
        // New-project-only — the SDK rejects productVariantBlueprintId alongside a real projectId (architecture.md §13).
        editorConfig.productVariantBlueprintId = currentEditorBlueprintHash;
        editorConfig.addonVariantIds = []; // deliberately always empty, see architecture.md §13

        // Our own page's quantity selector is authoritative (see goToCartFinished) — seed PrintBox's own display with it.
        const quantityInput = document.getElementById('Quantity-' + config.sectionId);
        const initialQuantity = quantityInput && parseInt(quantityInput.value, 10);
        if (initialQuantity) editorConfig.initialQuantity = initialQuantity;

        const components = {};

        // Confirmed by PrintBox in writing (decisions log) — these 5 key names are correct, not a guess.
        const shipNameEl = document.getElementById('shipName');
        const startPortEl = document.getElementById('startPort');
        const endPortEl = document.getElementById('endPort');
        const startDateEl = document.getElementById('startDateId');
        const endDateEl = document.getElementById('endDateId');
        if (shipNameEl && shipNameEl.value) components.ship_name = stripBracketsFromShipName(shipNameEl.value);
        if (startPortEl && startPortEl.value) components.start_port = startPortEl.value;
        if (endPortEl && endPortEl.value) components.end_port = endPortEl.value;
        if (startDateEl && startDateEl.value) components.start_date = startDateEl.value;
        if (endDateEl && endDateEl.value) components.end_date = endDateEl.value;
        // start_end_ports/start_end_dates (spec v2.2 §13.3) tried 2026-07-30, reverted — no
        // visible effect in the editor, unlike these separate keys. See gotchas.md.

        const photobookTitleEl = document.getElementById('photobookTitle');
        if (photobookTitleEl && photobookTitleEl.value) {
          components.custom_title = photobookTitleEl.value;
        }

        // "SHIP NAME | DD Month YYYY - DD Month YYYY" — SHIP NAME alone, cruise line stripped (see
        // stripCruiseLineFromShipName above). Dates parsed as plain YYYY-MM-DD components, not `new Date()` —
        // avoids the local-timezone day-shift risk that already bit this file once (see docs/date-pickers.md).
        if (shipNameEl && shipNameEl.value && startDateEl && startDateEl.value && endDateEl && endDateEl.value) {
          const formattedStartDate = formatSpineDate(startDateEl.value);
          const formattedEndDate = formatSpineDate(endDateEl.value);
          if (formattedStartDate && formattedEndDate) {
            components.spine_text = stripCruiseLineFromShipName(stripBracketsFromShipName(shipNameEl.value)) +' | ' + formattedStartDate + ' - ' + formattedEndDate;
          }
        }

        // Full framed map (border/title baked in) → static per-album fallback image (custom.album_fallback_map) → bare inset.
        const coverImageUrl = window.CruiseLivePreviewCompositeImageUrl || config.albumFallbackMapUrl || window.CruiseLivePreviewLastImageUrl;
        if (coverImageUrl) {
          // map_holder takes the image URL directly — no upload step needed, confirmed 2026-07-29.
          components.map_holder = coverImageUrl;
        }

        if (Object.keys(components).length) {
          editorConfig.personalizedPresentationContent = { version: 1, components: components };
        }

        // Same cruise data + cover image, snapshotted for goToCartFinished to use later.
        pendingCruiseSnapshot = {
          properties: collectCruiseFormProperties(),
          coverImageUrl: coverImageUrl || ''
        };
      }

      log('calling setEditorConfig() with', editorConfig);
      window.printbox.setEditorConfig(editorConfig).then(function (resultConfig) {
        log('setEditorConfig() resolved, config set to', resultConfig);
      }).catch(function (err) {
        error('setEditorConfig() rejected', err);
      });
    }

    // Required or the page stays uninteractive after closing the popup.
    function deinitEditor() {
      // Cleared here so it only survives PrintBox's own "back" exit.
      try {
        if (sessionStorage.getItem(PRE_EDITOR_URL_KEY)) {
          log('deinitEditor(): clearing', PRE_EDITOR_URL_KEY, '(controlled close, no restore needed)');
          sessionStorage.removeItem(PRE_EDITOR_URL_KEY);
        }
      } catch (e) {}
      if (!editorBooted) return;
      log('deinit()ing editor');
      editorBooted = false;
      currentEditorProjectId = null;
      clearInterval(hashPollTimer);
      hashPollTimer = null;
      if (window.printbox && typeof window.printbox.deinit === 'function') {
        window.printbox.deinit().catch(function (err) {
          error('printbox.deinit() failed', err);
        });
      }
      // Pathname/routing params can be left polluted even on a plain close.
      const cleanPathname = new URL(config.pageUrl).pathname;
      const url = new URL(window.location.href);
      const isDirty = url.pathname !== cleanPathname || PRINTBOX_ROUTING_PARAMS.some(function (key) { return url.searchParams.has(key); });
      if (isDirty) {
        url.pathname = cleanPathname;
        PRINTBOX_ROUTING_PARAMS.forEach(function (key) { url.searchParams.delete(key); });
        history.replaceState(null, '', url.toString());
      }
    }

    // Switching projects requires deinit() first; same-project calls are a no-op.
    function openEditor(projectId) {
      if (!window.printbox) {
        error('PrintBox CDN script not loaded (window.printbox missing), cannot initialize editor');
        return;
      }

      // Cruise-fields URL, not bare href — logoRedirectUri already delivers more.
      try {
        const preEditorUrl = buildCruiseFieldsUrl().toString();
        sessionStorage.setItem(PRE_EDITOR_URL_KEY, preEditorUrl);
        log('openEditor(): saved', PRE_EDITOR_URL_KEY, '=', preEditorUrl);
      } catch (e) {}

      // Needed for cart-add whenever this ISN'T reopening an already-cart-added project (isEditingExistingCartItem
      // takes goToCartFinished's other branch and never reads these) — covers both a brand-new project AND
      // resuming a not-yet-added-to-cart draft ("Continue editing", or a plain reload mid-session), since the
      // editor never lets the option selection change once opened, so re-resolving from the current DOM state is
      // still correct. Previously gated on `!projectId` alone, which skipped this for the Continue-editing case
      // entirely and left currentEditorVariantId null forever, breaking cart-add on Order.
      currentEditorVariantId = null;
      currentEditorBlueprintHash = null;
      if (!isEditingExistingCartItem) {
        const resolvedVariant = resolvePhotobookVariant();
        currentEditorVariantId = resolvedVariant ? resolvedVariant.id : null;
        currentEditorBlueprintHash = resolvedVariant ? resolvedVariant.blueprintHash : null;
        if (resolvedVariant && !resolvedVariant.blueprintHash) {
          warn('resolved photobook variant has no blueprintHash (custom.prinbox_product_variant_blueprint_id metafield blank?)', resolvedVariant);
        } else if (!resolvedVariant) {
          warn('could not resolve a photobook_product variant from the checked option selectors');
        }
      }
      // Reopening a draft: seed the last-known additionalPages until a fresh getEcommerceProjectPrice() call updates it; brand-new project starts at 0.
      lastKnownAdditionalPages = projectId ? getStoredAdditionalPages(config.productId) : 0;
      // Same idea for the cruise-data snapshot — a brand-new project gets a fresh one from bootEditor() itself.
      pendingCruiseSnapshot = projectId ? getStoredCruiseSnapshot(config.productId) : null;

      if (editorBooted && currentEditorProjectId !== projectId) {
        log('switching project, deinit() first', { from: currentEditorProjectId, to: projectId });
        if (typeof window.printbox.deinit === 'function') {
          window.printbox.deinit().then(function () {
            bootEditor(projectId);
          }).catch(function (err) {
            error('printbox.deinit() failed, initializing over it anyway', err);
            bootEditor(projectId);
          });
        } else {
          warn('printbox.deinit not available, initializing over the existing session anyway');
          bootEditor(projectId);
        }
        return;
      }

      if (!editorBooted) {
        bootEditor(projectId);
      }
    }

    if (openBtn && modal) {
      openBtn.addEventListener('click', function () {
        // Same field checks + scroll-to-error as native Add to cart (sections/main-product.liquid).
        // Defined on window inside a 'load' listener, but this click always fires well after that.
        if (typeof window.validateAllFields === 'function' && !window.validateAllFields()) {
          return;
        }
        modal.show(openBtn);
        // Always starts a new project — any old draft is intentionally abandoned.
        clearStoredProjectId(config.productId);
        updateContinuePanelState();
        isEditingExistingCartItem = false;
        suppressContinueButton = false;
        openEditor('');
      });

      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          const storedProjectId = getStoredProjectId(config.productId);
          if (!storedProjectId) {
            warn('continue button clicked but no stored projectId found for this product');
            updateContinuePanelState();
            return;
          }
          modal.show(continueBtn);
          isEditingExistingCartItem = false;
          suppressContinueButton = false;
          openEditor(storedProjectId);
        });
      }

      updateContinuePanelState();

      // Auto-open if a project is already in the URL hash (returning/refreshed visitor).
      const hashProjectId = getProjectIdFromHash();
      // QR "Add photos from phone" link — SDK switches into photo-uploader mode on its own once setEditorConfig runs.
      const photoUploaderProjectId = new URLSearchParams(window.location.search).get('pbxPhotoUploader');
      if (hashProjectId) {
        log('projectId already in URL hash on load, auto-opening the popup');
        modal.show(openBtn);
        openEditor(hashProjectId);
      } else if (photoUploaderProjectId) {
        log('pbxPhotoUploader present on load, auto-opening the popup');
        modal.show(openBtn);
        openEditor(photoUploaderProjectId);
      }

      // modalClosed fires for any modal on the page — filter by our own [open] attribute.
      document.body.addEventListener('modalClosed', function () {
        if (!modal.hasAttribute('open')) {
          deinitEditor();
        }
      });

      // Secondary listener — the interval poll above is what's actually relied on.
      window.addEventListener('hashchange', syncStoredProjectIdFromHash);

      // Tracks UpCart drawer-side cart changes, which nothing else here covers.
      // One-shot readiness check silently never fired (script loads before UpCart attaches its API) — poll instead.
      function subscribeToUpcartCartChanges() {
        if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;
        window.upcartSubscribeCartUpdated(updateContinuePanelState);
        if (typeof window.upcartSubscribeCartOpened === 'function') {
          window.upcartSubscribeCartOpened(updateContinuePanelState);
        }
        if (typeof window.upcartSubscribeItemRemoved === 'function') {
          window.upcartSubscribeItemRemoved(updateContinuePanelState);
        }
        log('subscribed to UpCart cart-change events for the continue panel');
        return true;
      }

      if (!subscribeToUpcartCartChanges()) {
        let upcartReadyAttempts = 0;
        const upcartReadyPoll = setInterval(function () {
          upcartReadyAttempts += 1;
          if (subscribeToUpcartCartChanges() || upcartReadyAttempts > 40) {
            clearInterval(upcartReadyPoll);
            if (upcartReadyAttempts > 40) {
              warn('gave up waiting for UpCart\'s subscribe API — continue panel will not react to drawer-side cart changes');
            }
          }
        }, 500);
      }
    } else {
      error('open button or popup modal not found', { openBtn: !!openBtn, modal: !!modal });
    }
  });
})();
